import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../apiClient';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function StatusBadge({ status }) {
  const cfg = {
    complete:     { bg: '#DAFBE1', color: '#1A7F37', label: '✓ Complete' },
    failed:       { bg: '#FFEBE9', color: '#CF222E', label: '✗ Failed' },
    timeout:      { bg: '#FFF8C5', color: '#9A6700', label: '⏱ Timeout' },
    executing:    { bg: '#DDF4FF', color: '#0969DA', label: '⟳ Executing' },
    initializing: { bg: '#EFF2F5', color: '#57606A', label: '· Init' },
    publishing:   { bg: '#DDF4FF', color: '#0969DA', label: '↑ Publishing' },
  };
  const c = cfg[status] || { bg: '#EFF2F5', color: '#57606A', label: status };
  return (
    <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
      background: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
}

function ExecRow({ exec }) {
  const durationLabel = !exec.duration_ms ? '—'
    : exec.duration_ms < 1000  ? `${exec.duration_ms}ms`
    : exec.duration_ms < 60000 ? `${(exec.duration_ms / 1000).toFixed(1)}s`
    : `${Math.floor(exec.duration_ms / 60000)}m ${Math.round((exec.duration_ms % 60000) / 1000)}s`;

  const agentLabel = {
    discovery:       '🔍 Discovery',
    rationalization: '🧠 Assessment',
    requirements:    '📝 Requirements',
    coding:          '💻 Code Gen',
    testing:         '🧪 Testing',
    security:        '🔒 Security',
    quality_gate:    '🔬 Quality',
    iac:             '🏗️ IaC',
    kiro:            '🤖 Kiro',
  }[exec.agent_name] || `🤖 ${exec.agent_name}`;

  const isActive = ['executing', 'initializing', 'publishing'].includes(exec.status);

  return (
    <div style={{
      padding: '8px 12px',
      borderBottom: '1px solid #EFF2F5',
      background: isActive ? '#F0F8FF' : 'transparent',
      borderLeft: `3px solid ${
        exec.status === 'complete'  ? '#1A7F37' :
        exec.status === 'failed'    ? '#CF222E' :
        exec.status === 'timeout'   ? '#9A6700' :
        isActive                    ? '#0969DA' : '#D0D7DE'
      }`,
    }}>
      {/* Row 1: agent + app + status + duration */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
        <span style={{ fontWeight: 700, fontSize: 11, color: '#1A1F2E', minWidth: 130 }}>
          {agentLabel}
        </span>
        {exec.app_id && (
          <span style={{ fontSize: 10, fontWeight: 700, color: '#0969DA',
            background: '#DDF4FF', padding: '1px 6px', borderRadius: 4 }}>
            {exec.app_id}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          {exec.context_injected && (
            <span title="Wiki context injected into prompt" style={{ fontSize: 11 }}>📖</span>
          )}
          {exec.retries > 0 && (
            <span style={{ fontSize: 10, color: '#CF222E', fontWeight: 700 }}>↺{exec.retries}</span>
          )}
          <StatusBadge status={exec.status} />
          <span style={{ fontSize: 10, color: '#57606A', minWidth: 36, textAlign: 'right' }}>
            {durationLabel}
          </span>
        </div>
      </div>
      {/* Row 2: execution id + what it means */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#8B949E' }}>
          #{exec.execution_id}
        </span>
        <span style={{ fontSize: 10, color: '#57606A' }}>
          {exec.status === 'complete'  ? `Completed in ${durationLabel}` :
           exec.status === 'failed'    ? 'Failed — will retry or skip' :
           exec.status === 'timeout'   ? `Timed out after ${durationLabel}` :
           exec.status === 'executing' ? 'Calling kiro-cli…' :
           exec.status === 'publishing'? 'Saving result to Wiki…' :
           'Waiting to start'}
        </span>
        {exec.context_injected && (
          <span style={{ fontSize: 10, color: '#1A7F37' }}>· Wiki context enriched prompt</span>
        )}
      </div>
    </div>
  );
}

function StatsBar({ stats }) {
  const total = stats.total || 0;
  const successRate = total > 0 ? Math.round((stats.completed / total) * 100) : 0;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '8px 12px',
      background: '#F6F8FA', borderRadius: 8, flexWrap: 'wrap' }}>
      {[
        { label: 'Active',    value: stats.active,    color: '#0969DA' },
        { label: 'Total',     value: stats.total,     color: '#1A1F2E' },
        { label: 'Completed', value: stats.completed, color: '#1A7F37' },
        { label: 'Failed',    value: stats.failed,    color: stats.failed > 0 ? '#CF222E' : '#8B949E' },
        { label: 'Avg Time',  value: stats.avg_ms ? `${(stats.avg_ms/1000).toFixed(1)}s` : '—', color: '#57606A' },
        { label: 'Success',   value: `${successRate}%`, color: successRate >= 90 ? '#1A7F37' : '#9A6700' },
      ].map(s => (
        <div key={s.label} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.value}</div>
          <div style={{ fontSize: 9, color: '#8B949E', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function SandboxMonitor({ sseEvents }) {
  const [stats, setStats]     = useState({ active: 0, total: 0, completed: 0, failed: 0, avg_ms: 0 });
  const [history, setHistory] = useState([]);
  const [active, setActive]   = useState([]);
  const [tab, setTab]         = useState('active'); // active | history

  const fetchData = useCallback(async () => {
    try {
      const [sR, hR, aR] = await Promise.all([
        apiFetch(`/api/sandbox/stats`),
        apiFetch(`/api/sandbox/history?limit=30`),
        apiFetch(`/api/sandbox/active`),
      ]);
      setStats(await sR.json());
      const hd = await hR.json(); setHistory(hd.history || []);
      const ad = await aR.json(); setActive(ad.executions || []);
    } catch (e) {}
  }, []);

  // Refresh on sandbox events
  useEffect(() => {
    const last = sseEvents?.[sseEvents.length - 1];
    if (last?.type?.startsWith('sandbox_')) fetchData();
  }, [sseEvents, fetchData]);

  // Initial load + periodic refresh
  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 5000);
    return () => clearInterval(t);
  }, [fetchData]);

  const tabStyle = (t) => ({
    padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700,
    cursor: 'pointer', border: 'none',
    background: tab === t ? '#0969DA' : 'transparent',
    color: tab === t ? '#fff' : '#57606A',
  });

  const displayRows = tab === 'active' ? active : [...history].reverse();

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title-accent">🔲</span> Kiro Sandbox Runtime
        <span style={{ marginLeft: 8, fontSize: 10, color: '#57606A' }}>Layer 3 — Agent Runtime</span>
        {stats.active > 0 && (
          <span style={{ marginLeft: 8, fontSize: 10, background: '#DDF4FF',
            color: '#0969DA', padding: '1px 8px', borderRadius: 8, fontWeight: 700,
            animation: 'pulse-dot 1.2s ease infinite' }}>
            {stats.active} active
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button style={tabStyle('active')} onClick={() => setTab('active')}>
            Active {active.length > 0 && `(${active.length})`}
          </button>
          <button style={tabStyle('history')} onClick={() => setTab('history')}>
            History {history.length > 0 && `(${history.length})`}
          </button>
        </div>
      </div>

      <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>

        <StatsBar stats={stats} />

        {/* Rows */}
        <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #D0D7DE', borderRadius: 8, overflow: 'hidden' }}>
          {displayRows.length === 0 && (
            <div style={{ color: '#8B949E', fontStyle: 'italic', fontSize: 11,
              textAlign: 'center', padding: '24px 0' }}>
              {tab === 'active' ? 'No active executions — pipeline is idle' : 'No execution history yet — run the pipeline to see activity'}
            </div>
          )}
          {displayRows.map((exec, i) => (
            <ExecRow key={exec.execution_id || i} exec={exec} />
          ))}
        </div>

        {/* Lifecycle legend */}
        <div style={{ display: 'flex', gap: 8, fontSize: 10, color: '#8B949E',
          borderTop: '1px solid #EFF2F5', paddingTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontWeight: 700 }}>Lifecycle:</span>
          {['Initialize', 'Execute', 'Publish', 'Complete'].map((s, i, arr) => (
            <React.Fragment key={s}>
              <span style={{ color: '#0969DA', fontWeight: 600 }}>{s}</span>
              {i < arr.length - 1 && <span style={{ color: '#D0D7DE' }}>→</span>}
            </React.Fragment>
          ))}
          <span style={{ marginLeft: 'auto' }}>📖 Wiki context injected · ↺ retried</span>
        </div>
      </div>
    </div>
  );
}